import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, ChevronRight, Check, ArrowUpRight, Menu, X, Sparkle,
  Home, Sparkles, Truck, Building2, Sofa, Droplets, HardHat, KeyRound, Briefcase,
  Leaf, HeartHandshake, ShowerHead, GraduationCap, ShoppingBag, Warehouse, ShieldCheck, Utensils, Cross,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { PROVINCES, CITY_COUNT } from "@/data/provinces";
import { paths } from "@/lib/seo";

export const ICONS = {
  Home, Sparkles, Truck, Building2, Sofa, Droplets, HardHat, KeyRound, Briefcase,
  Leaf, HeartHandshake, ShowerHead, GraduationCap, ShoppingBag, Warehouse, ShieldCheck, Utensils, Cross,
};

const EASE = [0.22, 1, 0.36, 1];

export function Logo({ dark = false }) {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
      <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
        <rect width="34" height="34" rx="10" fill={dark ? "#22D3EE" : "#0F172A"} />
        <path d="M17 7.5c3.6 4.2 6.2 7.4 6.2 10.8a6.2 6.2 0 1 1-12.4 0c0-3.4 2.6-6.6 6.2-10.8z" fill={dark ? "#0F172A" : "#38BDF8"} />
        <circle cx="23.4" cy="12.4" r="1.6" fill={dark ? "#0F172A" : "#22D3EE"} />
      </svg>
      <span className="font-display text-lg font-extrabold tracking-tight">
        <span className={dark ? "text-white" : "text-slate-900"}>Tbay</span>
        <span className="text-cyan-600"> Cleaning Services</span>
      </span>
    </Link>
  );
}

const NAV = [
  { label: "Services", to: "/services" },
  { label: "Locations", to: "/locations" },
  { label: "Sectors", to: "/sectors" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl" data-testid="header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex" data-testid="main-nav">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}-link`}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/fr" data-testid="header-fr-link" title="Version française"
            className="flex h-10 items-center rounded-full border border-slate-200 px-3.5 font-mono text-xs font-semibold text-slate-600 transition-colors hover:border-cyan-600 hover:text-cyan-700">
            FR
          </Link>
          <a href={SITE.emailHref} data-testid="header-email-button" aria-label={`Email ${SITE.email}`}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-cyan-600 hover:text-cyan-700">
            <Mail className="h-4 w-4" />
          </a>
          <a href={SITE.phoneHref} data-testid="header-call-button"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cyan-700">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
        </div>
        <button type="button" data-testid="mobile-menu-button" aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 lg:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-6 lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase()}-link`}
                className="text-base font-medium text-slate-700">{n.label}</Link>
            ))}
          </nav>
          <div className="mt-6 flex flex-col gap-3">
            <a href={SITE.phoneHref} data-testid="mobile-call-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
              <Phone className="h-4 w-4" /> Call {SITE.phone}
            </a>
            <a href={SITE.emailHref} data-testid="mobile-email-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700">
              <Mail className="h-4 w-4" /> {SITE.email}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

const POPULAR_SEARCHES = [
  ["house cleaning Toronto", "/locations/ontario/toronto/house-cleaning"],
  ["commercial cleaning Vancouver", "/locations/british-columbia/vancouver/commercial-cleaning"],
  ["move out cleaning Calgary", "/locations/alberta/calgary/move-in-out-cleaning"],
  ["deep cleaning Montreal", "/locations/quebec/montreal/deep-cleaning"],
  ["maid service Ottawa", "/locations/ontario/ottawa/maid-service"],
  ["carpet cleaning Edmonton", "/locations/alberta/edmonton/carpet-cleaning"],
  ["window cleaning Mississauga", "/locations/ontario/mississauga/window-cleaning"],
  ["airbnb cleaning Kelowna", "/locations/british-columbia/kelowna/airbnb-cleaning"],
  ["pressure washing Halifax", "/locations/nova-scotia/halifax/pressure-washing"],
  ["janitorial services Winnipeg", "/locations/manitoba/winnipeg/janitorial-services"],
  ["eco friendly cleaning Victoria", "/locations/british-columbia/victoria/eco-friendly-cleaning"],
  ["post construction cleaning Surrey", "/locations/british-columbia/surrey/post-construction-cleaning"],
  ["office cleaning Quebec City", "/locations/quebec/quebec-city/commercial-cleaning"],
  ["move in cleaning Regina", "/locations/saskatchewan/regina/move-in-out-cleaning"],
  ["house cleaning Moncton", "/locations/new-brunswick/moncton/house-cleaning"],
  ["deep cleaning St. John's", "/locations/newfoundland-and-labrador/st-johns/deep-cleaning"],
];

export function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo dark />
            <p className="mt-5 max-w-sm text-sm leading-relaxed">
              {SITE.tagline}. Fully insured, background-checked local crews in {CITY_COUNT}+ Canadian
              communities — one phone call, coast to coast.
            </p>
            <div className="mt-6 space-y-3">
              <a href={SITE.phoneHref} data-testid="footer-call-link"
                className="flex items-center gap-3 text-white transition-colors hover:text-cyan-300">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300"><Phone className="h-4 w-4" /></span>
                <span className="font-semibold">{SITE.phone}</span>
              </a>
              <a href={SITE.emailHref} data-testid="footer-email-link"
                className="flex items-center gap-3 text-white transition-colors hover:text-cyan-300">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300"><Mail className="h-4 w-4" /></span>
                <span className="font-semibold">{SITE.email}</span>
              </a>
              <p className="flex items-center gap-3 text-sm">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-300"><MapPin className="h-4 w-4" /></span>
                Proudly Canadian — serving every province &amp; territory
              </p>
            </div>
          </div>
          <div className="lg:col-span-2">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Services</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={paths.service(s.slug)} data-testid={`footer-service-${s.slug}`}
                    className="transition-colors hover:text-white">{s.short}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Locations</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {PROVINCES.map((p) => (
                <li key={p.slug}>
                  <Link to={paths.province(p.slug)} data-testid={`footer-province-${p.slug}`}
                    className="transition-colors hover:text-white">
                    Cleaning Services in {p.name}
                    <span className="ml-2 text-xs text-slate-600">{p.cities.length} {p.cities.length === 1 ? "city" : "cities"}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Company</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/about" data-testid="footer-about-link" className="transition-colors hover:text-white">About Tbay Cleaning Services</Link></li>
              <li><Link to="/services" data-testid="footer-services-link" className="transition-colors hover:text-white">All Cleaning Services</Link></li>
              <li><Link to="/locations" data-testid="footer-locations-link" className="transition-colors hover:text-white">Service Areas Directory</Link></li>
              <li><Link to="/regions" data-testid="footer-regions-link" className="transition-colors hover:text-white">Regional Service Hubs</Link></li>
              <li><Link to="/sectors" data-testid="footer-sectors-link" className="transition-colors hover:text-white">Sectors We Serve</Link></li>
              <li><Link to="/insights" data-testid="footer-insights-link" className="transition-colors hover:text-white">Cleaning Insights & Guides</Link></li>
              <li><a href="/sitemap.xml" data-testid="footer-sitemap-link" className="transition-colors hover:text-white">Sitemap</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Popular searches</p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs" data-testid="footer-popular-searches">
            {POPULAR_SEARCHES.map(([label, to]) => (
              <Link key={to} to={to} className="text-slate-500 transition-colors hover:text-cyan-300">{label}</Link>
            ))}
          </div>
        </div>
        <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 {SITE.legalName} All rights reserved.</p>
          <p>Proudly Canadian — coast to coast to coast.</p>
        </div>
      </div>
    </footer>
  );
}

export function StickyMobileCTA() {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-xl sm:border-0 sm:bg-transparent sm:backdrop-blur-none" data-testid="sticky-mobile-cta">
      <div className="pointer-events-auto grid grid-cols-2 gap-2 p-3 sm:mx-auto sm:mb-5 sm:flex sm:w-max sm:items-center sm:gap-2 sm:rounded-full sm:border sm:border-slate-200 sm:bg-white/90 sm:p-2 sm:shadow-[0_16px_50px_-12px_rgba(15,23,42,0.4)] sm:backdrop-blur-xl">
        <a href={SITE.phoneHref} data-testid="sticky-call-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-700 sm:px-6">
          <Phone className="h-4 w-4" />
          <span className="sm:hidden">Call now</span>
          <span className="hidden sm:inline">Call {SITE.phone}</span>
        </a>
        <a href={SITE.emailHref} data-testid="sticky-email-button"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-cyan-600 hover:text-cyan-700 sm:px-6">
          <Mail className="h-4 w-4" />
          <span className="sm:hidden">Email us</span>
          <span className="hidden sm:inline">{SITE.email}</span>
        </a>
      </div>
    </div>
  );
}

export function Marquee({ items }) {
  const row = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-slate-200 bg-white py-4" data-testid="marquee" aria-hidden="true">
      <div className="marquee-track flex w-max gap-10 whitespace-nowrap">
        {row.map((t, i) => (
          <span key={i} className="flex items-center gap-10 font-mono text-xs font-medium uppercase tracking-[0.25em] text-slate-500">
            <Sparkle className="h-3 w-3 text-cyan-600" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Reveal({ children, delay = 0, y = 28, className }) {
  return (
    <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay, ease: EASE }}
      className={className}>
      {children}
    </motion.div>
  );
}

export function Rise({ children, delay = 0, className }) {
  return (
    <motion.div initial={{ y: 24 }} whileInView={{ y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay, ease: EASE }} className={className}>
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`font-mono text-xs font-semibold uppercase tracking-[0.25em] text-cyan-700 ${className}`}>
      {children}
    </p>
  );
}

export function CTAButtons({ prefix = "cta", dark = false, className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a href={SITE.phoneHref} data-testid={`${prefix}-call-button`}
        className={dark
          ? "inline-flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-cyan-300"
          : "inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-700"}>
        <Phone className="h-4 w-4" /> Call {SITE.phone}
      </a>
      <a href={SITE.emailHref} data-testid={`${prefix}-email-button`}
        className={dark
          ? "inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-cyan-300 hover:text-cyan-300"
          : "inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-cyan-600 hover:text-cyan-700"}>
        <Mail className="h-4 w-4" /> {SITE.email}
      </a>
    </div>
  );
}

export function CTABand({ title = "One call. Spotless.", lead, prefix = "cta-band" }) {
  return (
    <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid={prefix}>
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-12 sm:px-14 sm:py-16">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Free same-day quotes</p>
        <h2 className="mt-3 max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h2>
        {lead && <p className="mt-4 max-w-lg leading-relaxed text-slate-300">{lead}</p>}
        <CTAButtons dark className="mt-8" prefix={prefix} />
      </div>
    </section>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
      {items.map((it, i) => (
        <span key={`${it.name}-${i}`} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3 text-slate-400" />}
          {it.path ? (
            <Link to={it.path} className="transition-colors hover:text-cyan-700">{it.name}</Link>
          ) : (
            <span className="font-medium text-slate-700">{it.name}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function PageHero({ crumbs, eyebrow, title, lead, prefix }) {
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
        <Breadcrumbs items={crumbs} />
        {eyebrow && <Eyebrow className="mt-6">{eyebrow}</Eyebrow>}
        <h1 data-testid={`${prefix}-title`}
          className="mt-3 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl">
          {title}
        </h1>
        {lead && <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{lead}</p>}
        <CTAButtons className="mt-8" prefix={prefix} />
      </div>
    </header>
  );
}

export function FAQAccordion({ faqs, testid = "faq" }) {
  return (
    <Accordion type="single" collapsible className="w-full" data-testid={testid}>
      {faqs.map((f, i) => (
        <AccordionItem key={i} value={`item-${i}`} data-testid={`${testid}-item-${i}`}>
          <AccordionTrigger className="text-left font-medium text-slate-800">{f.q}</AccordionTrigger>
          <AccordionContent className="leading-relaxed text-slate-600">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function CheckList({ items, testid = "checklist" }) {
  return (
    <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2" data-testid={testid}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
            <Check className="h-3 w-3" />
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

export function ArrowCard({ to, icon, title, desc, testid }) {
  const Icon = ICONS[icon] || Sparkle;
  return (
    <Link to={to} data-testid={testid}
      className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40 hover:shadow-[0_16px_40px_-16px_rgba(2,132,199,0.25)]">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700 transition-colors group-hover:bg-cyan-600 group-hover:text-white">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      {desc && <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{desc}</p>}
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700">
        Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </Link>
  );
}
